import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../services/common/notification.service';
import { AccessRefreshService } from '../services/access/access-refresh.service';
import { COMPANY_READ_ONLY, isCompanyReadOnlyError } from '../../shared/utils/company-access.utils';

/**
 * Marca una petición cuyo 403 ya trata el componente (degradación esperada,
 * p. ej. los totales semanales de Mi asistencia): no se muestra aviso ni se
 * recargan los permisos. El error igual se propaga al componente.
 */
export const ACCESS_ERROR_HANDLED = new HttpContextToken<boolean>(() => false);

/** Códigos 403 del sistema de permisos (contrato §3). */
const ACCESS_ERROR_TYPES = new Set(['ACCESS_DENIED', 'MODULE_DISABLED', 'ACCESS_UNDECLARED']);
/**
 * Códigos 403 del acceso de soporte («Ver como», support-access.spec §2.4) y
 * su aviso: el API bloqueó la acción en la sesión de soporte; los permisos
 * del usuario no cambiaron.
 */
const SUPPORT_NOTICE_KEYS = new Map<string, string>([
  ['SUPPORT_READ_ONLY', 'support.notice.readOnly'],
  ['SUPPORT_ACTION_FORBIDDEN', 'support.notice.forbidden'],
]);
/**
 * Escritura durante el modo consulta del término de suscripción
 * (subscription-termination.spec.md del API): sólo el aviso. Los permisos ya vienen recortados
 * en `/access/me`, así que recargarlos no cambia nada.
 */
const COMPANY_READ_ONLY_NOTICE = 'subscriptionEnd.readOnlyNotice';
/** Métodos que representan una acción del usuario. */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
/** Ventana en la que no se repite el mismo aviso (una pantalla dispara varias llamadas). */
const NOTICE_WINDOW_MS = 4000;

/**
 * 403 del sistema de permisos: muestra un aviso (texto propio para
 * MODULE_DISABLED), recarga `/access/me` y NUNCA cierra la sesión. Otros 403
 * (p. ej. EMPLOYEE_MISMATCH) y los 401/402 siguen su camino sin cambios.
 *
 * 403 del modo soporte (SUPPORT_READ_ONLY / SUPPORT_ACTION_FORBIDDEN) y
 * COMPANY_READ_ONLY (suscripción terminada, modo consulta): sólo el aviso; no
 * recarga los permisos ni termina la sesión. COMPANY_TERMINATED lo resuelve
 * AuthInterceptor (página «El servicio terminó»).
 */
@Injectable()
export class AccessDeniedInterceptor implements HttpInterceptor {
  // Resolución diferida: AccessRefreshService depende (vía AccessService) de
  // HttpClient, que a su vez construye esta cadena de interceptores.
  private readonly injector = inject(Injector);
  private lastNotice: { type: string; at: number } | null = null;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 403) {
          this.handleForbidden(req, error);
        }
        return throwError(() => error);
      }),
    );
  }

  private handleForbidden(req: HttpRequest<unknown>, error: HttpErrorResponse): void {
    if (req.context.get(ACCESS_ERROR_HANDLED)) return;
    const supportType = supportErrorType(error);
    const supportNotice = supportType ? SUPPORT_NOTICE_KEYS.get(supportType) : undefined;
    if (supportType && supportNotice) {
      this.notify(supportType, supportNotice);
      return;
    }
    if (isCompanyReadOnlyError(error)) {
      this.notify(COMPANY_READ_ONLY, COMPANY_READ_ONLY_NOTICE);
      return;
    }
    const type = accessErrorType(error);
    if (!type || req.url.includes('/access/me')) return;
    // Sólo las ACCIONES del usuario (métodos que escriben) avisan. Las lecturas
    // de fondo al abrir una pantalla ya recibían 403 del API por su rol antes
    // de este sistema (p. ej. la ficha propia desde «Marcar»), fallaban en
    // silencio y el componente degrada: avisar ahí sería ruido en pantallas
    // que el usuario sí puede abrir. Si la pantalla entera dejó de estar
    // permitida, la recarga periódica o al volver a la pestaña lo resuelve.
    if (!WRITE_METHODS.has(req.method)) return;

    this.notify(
      type,
      type === 'MODULE_DISABLED' ? 'access.moduleDisabledNotice' : 'access.deniedNotice',
    );
    this.injector.get(AccessRefreshService).refresh('forbidden');
  }

  private notify(type: string, key: string): void {
    const now = Date.now();
    if (
      this.lastNotice &&
      this.lastNotice.type === type &&
      now - this.lastNotice.at < NOTICE_WINDOW_MS
    ) {
      return;
    }
    this.lastNotice = { type, at: now };
    const translate = this.injector.get(TranslateService);
    this.injector.get(NotificationService).warning(translate.instant(key), 5000);
  }
}

/** Tipo del error del API (`{ error: { type } }`), sólo si es de permisos. */
export function accessErrorType(error: HttpErrorResponse): string | null {
  const type = apiErrorType(error);
  return type !== null && ACCESS_ERROR_TYPES.has(type) ? type : null;
}

/** Tipo del error del API, sólo si es un bloqueo del modo soporte. */
export function supportErrorType(error: HttpErrorResponse): string | null {
  const type = apiErrorType(error);
  return type !== null && SUPPORT_NOTICE_KEYS.has(type) ? type : null;
}

/** `{ status: 'failed', error: { type, message, code } }` → `type` (el código del error). */
function apiErrorType(error: HttpErrorResponse): string | null {
  const body = error.error as { error?: { type?: unknown }; type?: unknown } | null;
  const type = body?.error?.type ?? body?.type;
  return typeof type === 'string' ? type : null;
}
